import { lazy, type ComponentType } from "react";

const CHUNK_FAIL_PATTERNS = [
  /Failed to fetch dynamically imported module/,
  /error loading dynamically imported module/i,
  /cannot find module/i,
  /loading chunk \d+ failed/i,
  /loading CSS chunk \d+ failed/i,
];

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const msg = error instanceof Error ? error.message : String(error);
  return CHUNK_FAIL_PATTERNS.some((p) => p.test(msg));
}

function reloadOnce() {
  const key = "_th_chunk_reload";
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch {
    // ignore
  }
  window.location.reload();
}

// Listen to global errors that might slip past React boundaries
if (typeof window !== "undefined") {
  const handler = (event: ErrorEvent) => {
    if (isChunkLoadError(event.error)) {
      event.preventDefault();
      reloadOnce();
    }
  };
  window.addEventListener("error", handler);
}

/**
 * Drop-in replacement for React.lazy that automatically reloads the page
 * when a code-split chunk fails to load (e.g. after a new deploy).
 */
export function safeLazy<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
): ReturnType<typeof lazy<T>> {
  return lazy(() =>
    factory().catch((err) => {
      if (isChunkLoadError(err)) {
        reloadOnce();
      }
      throw err;
    })
  );
}
